import { z } from 'zod';
import { CurrencySchema } from './money';

/**
 * 推荐码分润（docs/11-推荐分润与财务推送.md）。
 * 推荐码：6 位，字符集去掉易混淆的 0/O、1/I/L。
 */
export const REFERRAL_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';
export const REFERRAL_CODE_LENGTH = 6;
export const REFERRAL_CODE_RE = /^[23456789ABCDEFGHJKMNPQRSTUVWXYZ]{6}$/;

/** 用户输入的推荐码规范化：去空白、转大写；格式不符返回 null。 */
export function normalizeReferralCode(input: string | null | undefined): string | null {
  if (!input) return null;
  const v = input.replace(/\s+/g, '').toUpperCase();
  return REFERRAL_CODE_RE.test(v) ? v : null;
}

export const ReferralTierSchema = z.object({
  /** 被推荐人注册后的第几个月（1 = 注册当月起算的第一个自然月周期） */
  month: z.number().int().min(1).max(120),
  /** 分润比例（百分比，最多 2 位小数），按平台毛利计算 */
  pct: z.number().min(0).max(100),
});
export type ReferralTier = z.infer<typeof ReferralTierSchema>;

export const ReferralSettingsSchema = z.object({
  enabled: z.boolean(),
  tiers: z
    .array(ReferralTierSchema)
    .max(36)
    .refine((tiers) => new Set(tiers.map((t) => t.month)).size === tiers.length, '月份不能重复'),
});
export type ReferralSettings = z.infer<typeof ReferralSettingsSchema>;

export const DEFAULT_REFERRAL_SETTINGS: ReferralSettings = {
  enabled: true,
  tiers: [
    { month: 1, pct: 30 },
    { month: 2, pct: 10 },
  ],
};

export const COMMISSION_STATUSES = ['accrued', 'settled', 'reversed'] as const;
export type CommissionStatus = (typeof COMMISSION_STATUSES)[number];
/** commission：任务结算产生；clawback：已结算佣金对应的任务退款后冲回（负数，抵扣下次结算） */
export const COMMISSION_KINDS = ['commission', 'clawback'] as const;
export type CommissionKind = (typeof COMMISSION_KINDS)[number];

/** 用户控制台看到的分润记录：不暴露平台成本与毛利 */
export const ReferralCommissionSchema = z.object({
  id: z.string(),
  kind: z.enum(COMMISSION_KINDS),
  referee: z.string(),
  month_index: z.number(),
  pct: z.number(),
  amount: z.string(),
  currency: CurrencySchema,
  status: z.enum(COMMISSION_STATUSES),
  created_at: z.string(),
  settled_at: z.string().nullable(),
});
export type ReferralCommission = z.infer<typeof ReferralCommissionSchema>;

export const ReferralSummarySchema = z.object({
  code: z.string(),
  link: z.string(),
  enabled: z.boolean(),
  tiers: z.array(ReferralTierSchema),
  currency: CurrencySchema,
  referees: z.number(),
  /** 待结算（含冲回抵扣） */
  accrued: z.string(),
  settled: z.string(),
  referred_by_code: z.string().nullable(),
});
export type ReferralSummary = z.infer<typeof ReferralSummarySchema>;

// ───────────── 管理端 ─────────────

export const AdminReferrerSchema = z.object({
  user_id: z.string(),
  email: z.string(),
  code: z.string().nullable(),
  referees: z.number(),
  accrued: z.string(),
  accrued_count: z.number(),
  settled: z.string(),
  last_accrued_at: z.string().nullable(),
});
export type AdminReferrer = z.infer<typeof AdminReferrerSchema>;

export const AdminReferralCommissionSchema = z.object({
  id: z.string(),
  kind: z.enum(COMMISSION_KINDS),
  referrer_id: z.string(),
  referrer_email: z.string(),
  referee_id: z.string(),
  referee_email: z.string(),
  task_id: z.string().nullable(),
  month_index: z.number(),
  charged: z.string(),
  cost: z.string(),
  profit: z.string(),
  pct: z.number(),
  amount: z.string(),
  currency: CurrencySchema,
  status: z.enum(COMMISSION_STATUSES),
  settlement_id: z.string().nullable(),
  note: z.string().nullable(),
  created_at: z.string(),
  settled_at: z.string().nullable(),
  reversed_at: z.string().nullable(),
});
export type AdminReferralCommission = z.infer<typeof AdminReferralCommissionSchema>;

export const REFERRAL_SETTLEMENT_METHODS = ['quota', 'offline'] as const;
export type ReferralSettlementMethod = (typeof REFERRAL_SETTLEMENT_METHODS)[number];

export const AdminReferralSettlementSchema = z.object({
  id: z.string(),
  referrer_id: z.string(),
  referrer_email: z.string(),
  method: z.enum(REFERRAL_SETTLEMENT_METHODS),
  amount: z.string(),
  currency: CurrencySchema,
  commission_count: z.number(),
  ledger_entry_id: z.string().nullable(),
  note: z.string().nullable(),
  operator: z.string().nullable(),
  created_at: z.string(),
});
export type AdminReferralSettlement = z.infer<typeof AdminReferralSettlementSchema>;

export const SettleReferralSchema = z.object({
  method: z.enum(REFERRAL_SETTLEMENT_METHODS),
  note: z.string().trim().max(500).optional(),
  /** 客户端生成的幂等键：同键重放返回同一次结算 */
  idempotency_key: z.string().min(8).max(64),
});
export type SettleReferral = z.infer<typeof SettleReferralSchema>;

export const AdminUserReferralSchema = z.object({
  code: z.string().nullable(),
  referred_by: z.object({ id: z.string(), email: z.string(), code: z.string().nullable() }).nullable(),
  referred_at: z.string().nullable(),
  referees: z.number(),
  accrued: z.string(),
  settled: z.string(),
});
export type AdminUserReferral = z.infer<typeof AdminUserReferralSchema>;

